import { DateTime } from 'luxon';
import { Channels } from 'server-schemas';
import { searchDataStore } from '../stores';
import { createStore } from 'zustand';
import { BFFFlightDetail, FlightSearchBFFResponse } from 'server-schemas/search';
import { calculateDurationBetweenDates } from './utils/date-time-helpers';
import { PassengerType } from 'server-schemas/stimson/session';
import { getStandardFareDetail } from 'server-lib/domain/utils/flightDetailUtils';

type Airport = {
  name: string;
  canonicalUrlToken: string;
  code: string;
  country: string;
  countryCode: string;
  airportGroupCode: string;
};

type AirportGroup = {
  name: string;
  code: string;
  countryCode: string;
  areaCode: string;
};

type FlightGridProps = {
  maxSeatsAvailable: number;
  enableWasNowPrices: boolean;
};

type BasketProps = {
  enableWasNowPrices: boolean;
};

export type GTMSharedContext = {
  flightGridProps?: FlightGridProps;
  flightSearchResponse: () => Omit<FlightSearchBFFResponse, 'X-DLX-Session-ID'>;
  basket?: Channels.Basket.BasketResponse;
  airportGroups: AirportGroup[];
  airports: Airport[];
  getFlights: () => BFFFlightDetail[];
  setBasket: (basket: Channels.Basket.BasketResponse) => void;
  clearBasket: () => void;
  setAirportGroups: (groups: AirportGroup[]) => void;
  setAirports: (airports: Airport[]) => void;
  noOfFlights: () => string;
  isMarketGroup: (iata: string) => boolean;
  getIatasForMarketGroup: (iata: string) => string[];
  findAirportByIata: (iata: string) => Airport | undefined;
  findAirportGroupByIata: (iata: string) => AirportGroup | undefined;
  getVariant: (iata: string, delimiter: string) => string | undefined;
  getTripDuration: (isOneWay: boolean, departureDate: DateTime, returnDate?: DateTime) => string;
  getPrices: (flights: BFFFlightDetail[]) => { minPrice: number; maxPrice: number };
  getMinPrices: () => string;
  getMaxPrices: () => string;
  isIataInMarketGroup: (airportCode: string) => boolean;
  isSearchedMultipleAirports: (airportCode: string) => string;
  noOfIatasForMarketGroup: (airportCode: string) => number | string;
  getNoOfSoldOutFlights: () => string;
  getOutboundFlights: () => BFFFlightDetail[];
  getInboundFlights: () => BFFFlightDetail[];
  getAirportCountryCode: (airportCode: string) => string | undefined;
  getFlightPosition: (flight: BFFFlightDetail, isOutbound: boolean) => string;
  getPriceVariance: (flight: BFFFlightDetail, isOutbound: boolean) => string;
  getBasketFlightPrice: () => string;
  getFlightBySegmentId: (segmentId: string) => BFFFlightDetail | undefined;
  getEJPlusPassengers: () => string;
  basketProps?: BasketProps;
};

export const GTMSharedContextStore = createStore<GTMSharedContext>((set, get) => ({
  airportGroups: [],
  airports: [],
  flightSearchResponse: () => ({
    journeyPairs: searchDataStore.getState().journeyPairs,
    metaData: searchDataStore.getState().metaData
  }),
  setBasket: basket => set(state => ({ ...state, basket })),
  clearBasket: () => set(state => ({ ...state, basket: undefined })),
  setAirports: airports => set(state => ({ ...state, airports })),
  setAirportGroups: airportGroups => set(state => ({ ...state, airportGroups })),
  isMarketGroup: iata => {
    return get().airportGroups?.filter(group => group.code === iata).length > 0;
  },
  getIatasForMarketGroup: groupCode =>
    get()
      .airports.filter(airport => airport.airportGroupCode === groupCode)
      .map(airport => airport.code) || [],
  findAirportByIata: iata => get().airports.find(airport => airport.code === iata),
  findAirportGroupByIata: iata => get().airportGroups.find(airport => airport.code === iata),
  getVariant: (iata, delimiter) => {
    const variantsLimit = 3;
    if (get().isMarketGroup(iata)) {
      const allIatas = get().getIatasForMarketGroup(iata);
      const variants = allIatas
        .map(item => get().findAirportByIata(item)?.countryCode)
        .slice(0, variantsLimit)
        .reduce((a, b) => `${a}${delimiter}${b}`, '');
      return variants?.length ? variants : '';
    }
    return get().findAirportByIata(iata)?.code;
  },
  noOfFlights: () => {
    const outboundFlights = get().getOutboundFlights();
    const inboundFlights = get().getInboundFlights();
    return inboundFlights.length === 0 ? `${outboundFlights.length}` : `${outboundFlights.length}|${inboundFlights.length}`;
  },
  getNoOfSoldOutFlights: () => {
    const outboundFlights = get().getOutboundFlights();
    const inboundFlights = get().getInboundFlights();
    const countSoldOutFlights = (flights: BFFFlightDetail[]) => {
      return flights.length ? flights.filter(flight => flight.soldOut).length : 0;
    };
    const noOfSoldOutOutboundFlight = countSoldOutFlights(outboundFlights);
    const noOfSoldOutInboundFlight = countSoldOutFlights(inboundFlights);
    return inboundFlights.length === 0
      ? `${noOfSoldOutOutboundFlight}`
      : `${noOfSoldOutOutboundFlight}|${noOfSoldOutInboundFlight}`;
  },
  getTripDuration: (isOneWay: boolean, departureDate: DateTime, returnDate?: DateTime) => {
    if (isOneWay || !returnDate) {
      return '';
    }

    return calculateDurationBetweenDates(returnDate, departureDate).toString();
  },
  getPrices: flights => {
    if (flights && flights.length) {
      const prices = flights.reduce((pricesArray, flight) => {
        // FIXME: need information about disrupted flights?
        // Do we need to add FLEXI to this calculation?
        if (!flight.soldOut && flight.fares!.ADT) {
          pricesArray.push(getStandardFareDetail(flight.fares!.ADT)!.unitPrice.grossPrice);
        }

        return pricesArray;
      }, [] as number[]);

      if (prices.length) {
        return {
          minPrice: Math.min(...prices),
          maxPrice: Math.max(...prices)
        };
      }
    }

    return {
      minPrice: 0,
      maxPrice: 0
    };
  },
  getMinPrices: () => {
    const outboundFlights = get().getOutboundFlights();
    const inboundFlights = get().getInboundFlights();
    const minOutboundFlightFare = get().getPrices(outboundFlights).minPrice;
    const minInboundFlightFare = get().getPrices(inboundFlights).minPrice;
    return inboundFlights.length === 0 ? `${minOutboundFlightFare}` : `${minOutboundFlightFare}|${minInboundFlightFare}`;
  },
  getMaxPrices: () => {
    const outboundFlights = get().getOutboundFlights();
    const inboundFlights = get().getInboundFlights();
    const maxOutboundFlightFare = get().getPrices(outboundFlights).maxPrice;
    const maxInboundFlightFare = get().getPrices(inboundFlights).maxPrice;
    return inboundFlights.length === 0 ? `${maxOutboundFlightFare}` : `${maxOutboundFlightFare}|${maxInboundFlightFare}`;
  },
  getFlights: () =>
    get()
      .flightSearchResponse()
      .journeyPairs.map(journey =>
        Object.values(journey.outbound.flights)
          .flat()
          .concat(Object.values(journey.inbound?.flights || []).flat())
          .flat()
      )
      .flat() || [],
  getOutboundFlights: () =>
    get()
      .flightSearchResponse()
      .journeyPairs.map(journey => Object.values(journey.outbound.flights).flat())
      .flat() || [],
  getInboundFlights: () =>
    get()
      .flightSearchResponse()
      .journeyPairs.map(journey => Object.values(journey.inbound?.flights || []).flat())
      .flat() || [],
  isReturnFlight: () => {
    get().flightSearchResponse();
  },
  getFlightPosition: ({ flightIdentification: flightIdToSearch }, isOutbound) => {
    const flightsToSearchFrom = isOutbound ? get().getOutboundFlights() : get().getInboundFlights();
    const flightZeroBasedIndexPosition = flightsToSearchFrom.findIndex(
      ({ flightIdentification: flightId }) => flightId === flightIdToSearch
    );

    // return the string of flight position as 1-index based position
    // (this is the most performant way to convert a number to a string in JS)
    // source: https://stackoverflow.com/a/5765401
    return flightZeroBasedIndexPosition + 1 + '';
  },
  getPriceVariance: (selectedFlight, isOutbound) => {
    const journeyType = isOutbound ? 'outboundJourney' : 'inboundJourney';
    const basket = get().basket;

    if (!basket || !basket.journeyPairs?.[journeyType]) {
      return '0';
    }

    const unitPriceOfPreviousSelectedPrice = basket?.journeyPairs[journeyType]?.airfare.airfares[0].unitPrice.raw;
    const previousSelectedFlightPrice: number = unitPriceOfPreviousSelectedPrice ?? 0;
    // Re-calculate flightVariance when Flexi, Children or Infants are in scope
    const selectedFlightPriceForAdult = selectedFlight.fares?.ADT?.STANDARD?.unitPrice.grossPrice;

    const priceDifferenceBetweenCurrentAndPreviousFlight = selectedFlightPriceForAdult! - previousSelectedFlightPrice;

    return priceDifferenceBetweenCurrentAndPreviousFlight.toFixed(2);
  },
  getBasketFlightPrice: () => {
    const outboundFlightPrice = get().basket?.journeyPairs.outboundJourney?.airfare.airfares[0].unitPrice.raw;
    const inboundFlightPrice = get().basket?.journeyPairs.isRoundtrip
      ? get().basket?.journeyPairs.inboundJourney?.airfare.airfares[0].unitPrice.raw
      : 0;
    return outboundFlightPrice ? (outboundFlightPrice + inboundFlightPrice!).toFixed(2).toString() : '';
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isIataInMarketGroup: airportCode => {
    const airportGroups = get().airportGroups;
    return Boolean(airportCode && airportGroups.some(airport => airport.code === airportCode));
  },
  isSearchedMultipleAirports: airportCode => (get().isIataInMarketGroup(airportCode) ? 'Yes' : 'No'),
  noOfIatasForMarketGroup: airportCode => get().getIatasForMarketGroup(airportCode).length,
  getAirportCountryCode: airportCode => {
    return get().isIataInMarketGroup(airportCode)
      ? get().findAirportGroupByIata(airportCode)?.countryCode
      : get().findAirportByIata(airportCode)?.countryCode;
  },
  getFlightBySegmentId: (segmentId: string) => {
    return get()
      .flightSearchResponse()
      .journeyPairs.map(pair => {
        return Object.values(pair.outbound.flights)
          .flat()
          .concat(Object.values(pair.inbound?.flights || []).flat());
      })
      .flat()
      .find(flight => flight.flightSegmentId === segmentId);
  },
  getEJPlusPassengers: () => {
    const ejPlusAdults = get().basket?.passengers?.filter(
      passenger => passenger.Type == PassengerType.Adult && passenger.EjPlusNumber
    ).length;
    const ejPlusChildren = get().basket?.passengers?.filter(
      passenger => passenger.Type == PassengerType.Child && passenger.EjPlusNumber
    ).length;
    return `${ejPlusAdults || '0'}|${ejPlusChildren || '0'}`;
  }
}));
